import { Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import AuthFrame from '@/components/auth/AuthFrame';
import useAuth from '@/hooks/useAuth';
import { forgotPassword, toAuthApiError } from '@/services/authApi';
import { URL } from '@/types';

type ForgotForm = { email: string };
/** Login の password_reset_required から渡される */
type ForgotState = { email?: string; resetRequired?: boolean } | null;

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: ForgotState };
  const { validationRules, errorMessage } = useAuth();
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const { control, handleSubmit } = useForm<ForgotForm>({
    defaultValues: { email: state?.email ?? '' },
    mode: 'onChange',
  });

  const onSubmit = async ({ email }: ForgotForm) => {
    setError(undefined);
    setSubmitting(true);
    try {
      await forgotPassword(email);
      navigate(URL.RESET_PASSWORD, { state: { email, sent: true } });
    } catch (e) {
      setError(errorMessage(toAuthApiError(e).code));
      setSubmitting(false);
    }
  };

  // t() の戻り型 DefaultTFuncReturn（string | null）は AuthFrame の info（string | undefined）と衝突するため、
  // 変数に一度受けて渡す（既存キーは必ず文字列を返すので String() での挙動変化は無い。useAuth.ts と同じ流儀）
  const info = state?.resetRequired
    ? String(t('pages:auth:forgot_password:reset_required'))
    : undefined;

  return (
    <AuthFrame title={t('pages:auth:forgot_password:title')} info={info} error={error}>
      <Typography sx={{ mb: 3 }}>{t('pages:auth:forgot_password:lead')}</Typography>
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ mb: 4 }}>
          <Controller
            name="email"
            control={control}
            rules={validationRules.email}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                type="email"
                autoComplete="email"
                label={t('pages:auth:labels:email')}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button type="submit" color="primary" variant="contained" disabled={submitting}>
            {t('pages:auth:forgot_password:submit')}
          </Button>
        </Box>
        <Stack alignItems="center">
          <Link component={RouterLink} to={URL.LOGIN}>
            {t('pages:auth:forgot_password:back_to_login')}
          </Link>
        </Stack>
      </Box>
    </AuthFrame>
  );
}
